<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const isSubmitting = ref(false);
const searchQuery = ref('');

// Data from store - index now returns rooms with nested rate_types
const roomsWithRates = computed(
  () => getters['motorReservaRoomRates/getRecords'].value
);

const filteredRoomsWithRates = computed(() => {
  if (!searchQuery.value.trim()) return roomsWithRates.value;
  const q = searchQuery.value.toLowerCase();
  return roomsWithRates.value.filter(
    r =>
      r.name.toLowerCase().includes(q) ||
      (r.rate_types || []).some(rt =>
        (rt.rate_name || '').toLowerCase().includes(q)
      )
  );
});
const allRates = computed(() => getters['motorReservaRates/getRecords'].value);
const uiFlags = computed(
  () => getters['motorReservaRoomRates/getUIFlags'].value
);

// Per-room editing state
const editingRoomId = ref(null);
const selectedRateId = ref(null);
const selectedMasterRateId = ref(null);
const amountPercentage = ref(0);
const amountValue = ref(0);
const amountDivision = ref(0);

// Mass combine modal
const showMassModal = ref(false);
const massRateId = ref(null);
const massWithDerivation = ref(false);
const massMasterRateId = ref(null);
const massPercentage = ref(0);
const massValue = ref(0);
const massDivision = ref(0);

// Computed: available rates for a room (not already linked)
const availableRatesForRoom = computed(() => {
  if (!editingRoomId.value) return [];
  const room = roomsWithRates.value.find(r => r.id === editingRoomId.value);
  if (!room) return allRates.value;
  const linkedIds = (room.rate_types || []).map(rt => rt.motor_reserva_rate_id);
  return allRates.value.filter(r => !linkedIds.includes(r.id));
});

// Computed: all rates for master rate dropdown (+ option "none")
const masterRateOptions = computed(() => {
  return allRates.value;
});

const hasMasterRate = computed(() => {
  return selectedMasterRateId.value && selectedMasterRateId.value !== '';
});

// Open inline form for a specific room
const openAddForm = room => {
  editingRoomId.value = room.id;
  selectedRateId.value = null;
  selectedMasterRateId.value = '';
  amountPercentage.value = 0;
  amountValue.value = 0;
  amountDivision.value = 0;
};

const cancelAddForm = () => {
  editingRoomId.value = null;
};

// Confirm linking a rate to a room
const confirmRoomRate = async room => {
  if (!selectedRateId.value) return;

  // Anti-circular validation
  if (
    selectedMasterRateId.value &&
    String(selectedMasterRateId.value) === String(selectedRateId.value)
  ) {
    useAlert('A tarifa master não pode ser igual à tarifa principal.');
    return;
  }

  isSubmitting.value = true;
  try {
    const payload = {
      motor_reserva_room_rate: {
        motor_reserva_room_id: room.id,
        motor_reserva_rate_id: selectedRateId.value,
        master_rate_id: selectedMasterRateId.value || null,
        amount_percentage: amountPercentage.value || 0,
        amount_value: amountValue.value || 0,
        amount_division: amountDivision.value || 0,
      },
    };
    await store.dispatch('motorReservaRoomRates/create', payload);
    useAlert(t('MOTOR_RESERVA.ROOM_RATES.ADD.API.SUCCESS_MESSAGE'));
    editingRoomId.value = null;
  } catch (error) {
    useAlert(
      error?.message || t('MOTOR_RESERVA.ROOM_RATES.ADD.API.ERROR_MESSAGE')
    );
  } finally {
    isSubmitting.value = false;
  }
};

// Delete a combination
const deleteRoomRate = async rr => {
  // eslint-disable-next-line no-restricted-globals, no-alert
  if (!confirm(t('MOTOR_RESERVA.ROOM_RATES.DELETE.CONFIRM'))) return;
  try {
    await store.dispatch('motorReservaRoomRates/delete', rr.id);
    useAlert(t('MOTOR_RESERVA.ROOM_RATES.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(
      error?.message || t('MOTOR_RESERVA.ROOM_RATES.DELETE.API.ERROR_MESSAGE')
    );
  }
};

// Mass combine
const openMassModal = () => {
  massRateId.value = null;
  massWithDerivation.value = false;
  massMasterRateId.value = null;
  massPercentage.value = 0;
  massValue.value = 0;
  massDivision.value = 0;
  showMassModal.value = true;
};

const closeMassModal = () => {
  showMassModal.value = false;
};

const submitMassCombine = async () => {
  if (!massRateId.value) return;

  const combinations = [];
  roomsWithRates.value.forEach(room => {
    const alreadyLinked = (room.rate_types || []).some(
      rt => String(rt.motor_reserva_rate_id) === String(massRateId.value)
    );
    if (alreadyLinked) return;

    combinations.push({
      motor_reserva_room_id: room.id,
      motor_reserva_rate_id: massRateId.value,
      master_rate_id: massWithDerivation.value ? massMasterRateId.value : null,
      amount_percentage: massWithDerivation.value ? massPercentage.value : 0,
      amount_value: massWithDerivation.value ? massValue.value : 0,
      amount_division: massWithDerivation.value ? massDivision.value : 0,
    });
  });

  if (combinations.length === 0) {
    useAlert('Todos os quartos já possuem esta tarifa vinculada.');
    return;
  }

  isSubmitting.value = true;
  try {
    await store.dispatch('motorReservaRoomRates/massCombine', combinations);
    useAlert(t('MOTOR_RESERVA.ROOM_RATES.MASS.API.SUCCESS_MESSAGE'));
    closeMassModal();
  } catch (error) {
    useAlert(
      error?.message || t('MOTOR_RESERVA.ROOM_RATES.MASS.API.ERROR_MESSAGE')
    );
  } finally {
    isSubmitting.value = false;
  }
};

onBeforeMount(() => {
  store.dispatch('motorReservaRoomRates/get');
  store.dispatch('motorReservaRates/get');
});
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h3 class="text-lg font-semibold text-slate-800 dark:text-slate-100">
          {{ t('MOTOR_RESERVA.ROOM_RATES.HEADER') }}
        </h3>
        <p class="text-sm text-slate-600 dark:text-slate-300">
          {{ t('MOTOR_RESERVA.ROOM_RATES.DESCRIPTION') }}
        </p>
      </div>
      <Button
        :label="t('MOTOR_RESERVA.ROOM_RATES.MASS_COMBINE')"
        @click="openMassModal"
      />
    </div>

    <!-- Busca -->
    <input
      v-model="searchQuery"
      type="text"
      class="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-800 dark:text-slate-100 focus:border-woot-500 focus:outline-none"
      :placeholder="t('MOTOR_RESERVA.ROOM_RATES.SEARCH_PLACEHOLDER')"
    />

    <!-- Loading -->
    <div
      v-if="uiFlags.isFetching"
      class="flex items-center justify-center py-8"
    >
      <span class="text-slate-500">{{
        t('MOTOR_RESERVA.COMMON.LOADING')
      }}</span>
    </div>

    <!-- Room list -->
    <div v-else class="flex flex-col gap-4">
      <div
        v-for="room in filteredRoomsWithRates"
        :key="room.id"
        class="border border-slate-200 dark:border-slate-600 rounded-lg overflow-hidden"
      >
        <!-- Room header -->
        <div
          class="bg-slate-100 dark:bg-slate-700 px-4 py-3 flex items-center justify-between"
        >
          <h4 class="text-sm font-semibold text-slate-800 dark:text-slate-100">
            {{ room.name }}
          </h4>
          <span
            v-if="!room.active"
            class="px-2 py-0.5 text-xs rounded-full bg-slate-200 text-slate-500"
          >
            {{ t('MOTOR_RESERVA.COMMON.INACTIVE') }}
          </span>
        </div>

        <div class="px-4 py-3">
          <p
            class="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2 uppercase tracking-wide"
          >
            {{ t('MOTOR_RESERVA.ROOM_RATES.LINKED_RATES') }}
          </p>

          <!-- Linked rates list -->
          <div
            v-if="room.rate_types && room.rate_types.length > 0"
            class="flex flex-col gap-2 mb-3"
          >
            <div
              v-for="rr in room.rate_types"
              :key="rr.id"
              class="flex items-center justify-between bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-600 rounded-lg px-3 py-2"
            >
              <div class="flex flex-col gap-0.5">
                <span
                  class="text-sm font-medium text-slate-700 dark:text-slate-200"
                >
                  {{ rr.rate_name }}
                </span>
                <!-- Master rate derivation info -->
                <div
                  v-if="rr.master_rate_id"
                  class="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400"
                >
                  <span class="font-medium">{{
                    t('MOTOR_RESERVA.ROOM_RATES.MASTER_RATE_LABEL')
                  }}</span>
                  <span class="text-woot-500">{{ rr.master_rate_name }}</span>
                  <span
                    v-if="
                      rr.amount_percentage && Number(rr.amount_percentage) !== 0
                    "
                  >
                    {{
                      `${t('MOTOR_RESERVA.ROOM_RATES.AMOUNT_PERCENTAGE')}: ${rr.amount_percentage}%`
                    }}
                  </span>
                  <span v-if="rr.amount_value && Number(rr.amount_value) !== 0">
                    {{
                      `${t('MOTOR_RESERVA.ROOM_RATES.AMOUNT_VALUE')}: ${rr.amount_value}`
                    }}
                  </span>
                  <span
                    v-if="
                      rr.amount_division && Number(rr.amount_division) !== 0
                    "
                  >
                    {{
                      `${t('MOTOR_RESERVA.ROOM_RATES.AMOUNT_DIVISION')}: ${rr.amount_division}`
                    }}
                  </span>
                </div>
              </div>
              <button
                class="text-xs text-red-500 hover:text-red-700 font-medium"
                @click="deleteRoomRate(rr)"
              >
                {{ t('MOTOR_RESERVA.COMMON.DELETE') }}
              </button>
            </div>
          </div>

          <p v-else class="text-xs text-slate-400 mb-3">
            {{ t('MOTOR_RESERVA.ROOM_RATES.NO_RATES') }}
          </p>

          <!-- Inline add form (visible when editing this room) -->
          <div
            v-if="editingRoomId === room.id"
            class="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg p-4 flex flex-col gap-3"
          >
            <!-- Row 1: Select rate + confirm/cancel -->
            <div class="flex items-end gap-3">
              <div class="flex-1 flex flex-col gap-1">
                <label
                  class="text-xs font-medium text-slate-600 dark:text-slate-300"
                >
                  {{ t('MOTOR_RESERVA.ROOM_RATES.SELECT_RATE') }}
                </label>
                <select
                  v-model="selectedRateId"
                  class="px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-sm"
                >
                  <option :value="null" disabled>
                    {{ t('MOTOR_RESERVA.ROOM_RATES.SELECT_RATE') }}
                  </option>
                  <option
                    v-for="rate in availableRatesForRoom"
                    :key="rate.id"
                    :value="rate.id"
                  >
                    {{ rate.name }}
                  </option>
                </select>
              </div>
              <div class="flex gap-2">
                <button
                  class="px-3 py-2 bg-green-500 hover:bg-green-600 text-white text-sm rounded-lg"
                  :disabled="isSubmitting"
                  @click="confirmRoomRate(room)"
                >
                  {{ t('MOTOR_RESERVA.COMMON.SAVE') }}
                </button>
                <button
                  class="px-3 py-2 bg-slate-200 hover:bg-slate-300 dark:bg-slate-600 dark:hover:bg-slate-500 text-slate-700 dark:text-slate-200 text-sm rounded-lg"
                  @click="cancelAddForm"
                >
                  {{ t('MOTOR_RESERVA.COMMON.CANCEL') }}
                </button>
              </div>
            </div>

            <!-- Row 2: Master rate select -->
            <div class="flex flex-col gap-1">
              <label
                class="text-xs font-medium text-slate-600 dark:text-slate-300"
              >
                {{ t('MOTOR_RESERVA.ROOM_RATES.SELECT_MASTER_RATE') }}
              </label>
              <select
                v-model="selectedMasterRateId"
                class="px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-sm max-w-md"
              >
                <option value="">
                  {{ t('MOTOR_RESERVA.ROOM_RATES.NO_MASTER_RATE') }}
                </option>
                <option
                  v-for="rate in masterRateOptions"
                  :key="rate.id"
                  :value="rate.id"
                >
                  {{ rate.name }}
                </option>
              </select>
            </div>

            <!-- Row 3: Derivation fields (visible only when master rate selected) -->
            <div v-if="hasMasterRate" class="grid grid-cols-3 gap-3">
              <div class="flex flex-col gap-1">
                <label
                  class="text-xs font-medium text-slate-600 dark:text-slate-300"
                >
                  {{ t('MOTOR_RESERVA.ROOM_RATES.AMOUNT_PERCENTAGE') }}
                </label>
                <input
                  v-model.number="amountPercentage"
                  type="number"
                  step="0.01"
                  class="px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-sm"
                />
              </div>
              <div class="flex flex-col gap-1">
                <label
                  class="text-xs font-medium text-slate-600 dark:text-slate-300"
                >
                  {{ t('MOTOR_RESERVA.ROOM_RATES.AMOUNT_VALUE') }}
                </label>
                <input
                  v-model.number="amountValue"
                  type="number"
                  step="0.01"
                  class="px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-sm"
                />
              </div>
              <div class="flex flex-col gap-1">
                <label
                  class="text-xs font-medium text-slate-600 dark:text-slate-300"
                >
                  {{ t('MOTOR_RESERVA.ROOM_RATES.AMOUNT_DIVISION') }}
                </label>
                <input
                  v-model.number="amountDivision"
                  type="number"
                  step="0.01"
                  class="px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-sm"
                />
              </div>
            </div>
          </div>

          <!-- Link rate action (visible when NOT editing this room) -->
          <button
            v-else
            class="text-sm text-woot-500 hover:text-woot-700 font-medium"
            @click="openAddForm(room)"
          >
            {{ t('MOTOR_RESERVA.ROOM_RATES.LINK_RATE') }}
          </button>
        </div>
      </div>

      <!-- Empty state -->
      <p
        v-if="filteredRoomsWithRates.length === 0"
        class="text-sm text-slate-500 text-center py-4"
      >
        {{ t('MOTOR_RESERVA.COMMON.EMPTY_ROOM_RATES') }}
      </p>
    </div>

    <!-- Mass Combine Modal -->
    <div
      v-if="showMassModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      @click.self="closeMassModal"
    >
      <div
        class="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-lg mx-4"
      >
        <!-- Modal header -->
        <div
          class="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-600"
        >
          <h3
            class="text-base font-semibold text-slate-800 dark:text-slate-100"
          >
            {{ t('MOTOR_RESERVA.ROOM_RATES.MASS.TITLE') }}
          </h3>
          <Button
            variant="link"
            size="small"
            :label="t('MOTOR_RESERVA.COMMON.CANCEL')"
            @click="closeMassModal"
          />
        </div>

        <!-- Modal body -->
        <div class="px-6 py-4 flex flex-col gap-4">
          <p class="text-sm text-slate-600 dark:text-slate-300">
            {{ t('MOTOR_RESERVA.ROOM_RATES.MASS.DESCRIPTION') }}
          </p>

          <!-- Select rate -->
          <div class="flex flex-col gap-1">
            <label
              class="text-xs font-medium text-slate-600 dark:text-slate-300"
            >
              {{ t('MOTOR_RESERVA.ROOM_RATES.MASS.SELECT_RATE') }}
            </label>
            <select
              v-model="massRateId"
              class="px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-sm"
            >
              <option :value="null" disabled>
                {{ t('MOTOR_RESERVA.COMMON.SELECT_RATE') }}
              </option>
              <option v-for="rate in allRates" :key="rate.id" :value="rate.id">
                {{ rate.name }}
              </option>
            </select>
          </div>

          <!-- Derivation radio -->
          <div class="flex flex-col gap-2">
            <label
              class="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200 cursor-pointer"
            >
              <input
                v-model="massWithDerivation"
                type="radio"
                :value="false"
                class="text-woot-500"
              />
              {{ t('MOTOR_RESERVA.ROOM_RATES.MASS.WITHOUT_DERIVATION') }}
            </label>
            <label
              class="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200 cursor-pointer"
            >
              <input
                v-model="massWithDerivation"
                type="radio"
                :value="true"
                class="text-woot-500"
              />
              {{ t('MOTOR_RESERVA.ROOM_RATES.MASS.WITH_DERIVATION') }}
            </label>
          </div>

          <!-- Derivation fields (visible when with derivation) -->
          <div v-if="massWithDerivation" class="flex flex-col gap-3 pl-6">
            <div class="flex flex-col gap-1">
              <label
                class="text-xs font-medium text-slate-600 dark:text-slate-300"
              >
                {{ t('MOTOR_RESERVA.ROOM_RATES.SELECT_MASTER_RATE') }}
              </label>
              <select
                v-model="massMasterRateId"
                class="px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-sm"
              >
                <option :value="null" disabled>
                  {{ t('MOTOR_RESERVA.COMMON.SELECT_RATE') }}
                </option>
                <option
                  v-for="rate in allRates"
                  :key="rate.id"
                  :value="rate.id"
                >
                  {{ rate.name }}
                </option>
              </select>
            </div>
            <div class="grid grid-cols-3 gap-3">
              <div class="flex flex-col gap-1">
                <label
                  class="text-xs font-medium text-slate-600 dark:text-slate-300"
                >
                  {{ t('MOTOR_RESERVA.ROOM_RATES.AMOUNT_PERCENTAGE') }}
                </label>
                <input
                  v-model.number="massPercentage"
                  type="number"
                  step="0.01"
                  class="px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-sm"
                />
              </div>
              <div class="flex flex-col gap-1">
                <label
                  class="text-xs font-medium text-slate-600 dark:text-slate-300"
                >
                  {{ t('MOTOR_RESERVA.ROOM_RATES.AMOUNT_VALUE') }}
                </label>
                <input
                  v-model.number="massValue"
                  type="number"
                  step="0.01"
                  class="px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-sm"
                />
              </div>
              <div class="flex flex-col gap-1">
                <label
                  class="text-xs font-medium text-slate-600 dark:text-slate-300"
                >
                  {{ t('MOTOR_RESERVA.ROOM_RATES.AMOUNT_DIVISION') }}
                </label>
                <input
                  v-model.number="massDivision"
                  type="number"
                  step="0.01"
                  class="px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-sm"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Modal footer -->
        <div
          class="flex justify-end gap-2 px-6 py-4 border-t border-slate-200 dark:border-slate-600"
        >
          <Button
            variant="secondary"
            :label="t('MOTOR_RESERVA.COMMON.CANCEL')"
            @click="closeMassModal"
          />
          <Button
            :label="t('MOTOR_RESERVA.ROOM_RATES.MASS.COMBINE')"
            :is-loading="isSubmitting"
            @click="submitMassCombine"
          />
        </div>
      </div>
    </div>
  </div>
</template>
